export type TemplateType = {
  id: string
  name: string
  description: string
  json: string
}

// Convert the template JSON into a string for storage
export const prebuiltTemplates: TemplateType[] = [
  {
    id: 'household-income',
    name: 'Household Income Data Form',
    description: 'A template for collecting household income information with dynamic fields based on household size.',
    json: JSON.stringify({
      title: {
        default: 'Household Income Data Form',
      },
      description: {
        default:
          "Thank you for participating in our Household Information Survey. Your input not only helps us serve you better but also plays a crucial role in securing additional funding for your students' schools from the state.",
      },
      logoWidth: '700px',
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
          title: {
            default: 'Household Information',
          },
          description: {
            default: '\n',
          },
          elements: [
            {
              type: 'dropdown',
              name: 'question2',
              title: {
                default: 'Snap/TANF Information',
              },
              description: {
                default: 'Do you or anyone in your household receive SNAP or TANF?',
              },
              isRequired: true,
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'Yes',
                  },
                },
                {
                  value: 'Item 2',
                  text: {
                    default: 'No',
                  },
                },
              ],
            },
            {
              type: 'text',
              name: 'question3',
              visibleIf: "{question2} = 'Item 2'",
              title: {
                default: 'Household Size',
              },
              description: {
                default: 'How many people are in your household (including all adults and children)?',
              },
              requiredIf: "{question2} = 'Item 2'",
              inputType: 'number',
              min: 1,
            },
            {
              type: 'panel',
              name: 'panel1',
              visibleIf: '{question3} >= 1 && {question3} < 9',
              elements: [
                {
                  type: 'expression',
                  name: 'question7',
                  title: {
                    default: 'Income Information',
                  },
                  description: {
                    default: 'Do you make more or less than ?',
                  },
                  expression: '{question3} *200',
                  format: {
                    default: 'Do you make more or less than {0}?',
                  },
                  displayStyle: 'currency',
                },
                {
                  type: 'dropdown',
                  name: 'question8',
                  title: {
                    default: '\n',
                  },
                  hideNumber: true,
                  choices: [
                    {
                      value: 'Item 1',
                      text: {
                        default: 'More',
                      },
                    },
                    {
                      value: 'Item 2',
                      text: {
                        default: 'Less',
                      },
                    },
                  ],
                },
              ],
            },
            {
              type: 'text',
              name: 'question5',
              visibleIf: '{question3} >= 9',
              title: {
                default: 'Income Information',
              },
              description: {
                default: 'Household Income:',
              },
              requiredIf: '{question3} >= 9',
              inputType: 'number',
            },
            {
              type: 'checkbox',
              name: 'question6',
              title: {
                default: 'Verification',
              },
              description: {
                default: 'Please confirm your information is accurate by checking the box below:\n',
              },
              isRequired: true,
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'I confirm the information is accurate',
                  },
                },
              ],
            },
          ],
        },
      ],
      widthMode: 'responsive',
      width: '12in',
    }),
  },
  {
    id: 'teacher-feedback',
    name: 'Teacher Feedback Survey',
    description: 'A template for teacher feedback survey for end of semester.',
    json: JSON.stringify({
      title: {
        default: 'Teacher Feedback Survey',
      },
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
          elements: [
            {
              type: 'text',
              name: 'question1',
              title: {
                default: 'Course Name',
              },
              isRequired: true,
            },
            {
              type: 'text',
              name: 'question3',
              title: {
                default: 'Teacher Name',
              },
            },
            {
              type: 'checkbox',
              name: 'question5',
              title: {
                default: "Which grade you're in",
              },
              isRequired: true,
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: '9',
                  },
                },
                {
                  value: 'Item 2',
                  text: {
                    default: '10',
                  },
                },
                {
                  value: 'Item 3',
                  text: {
                    default: '11',
                  },
                },
                {
                  value: 'Item 4',
                  text: {
                    default: '12',
                  },
                },
              ],
            },
            {
              type: 'expression',
              name: 'question4',
              title: {
                default:
                  'Please answer the following questions honestly. Your feedback helps improve teaching and learning!',
              },
              hideNumber: true,
              useGrouping: false,
            },
          ],
        },
        {
          name: 'page2',
          elements: [
            {
              type: 'checkbox',
              name: 'question2',
              title: {
                default: 'How clearly did the teacher explain the material?',
              },
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'Very clearly',
                  },
                },
                {
                  value: 'Item 2',
                  text: {
                    default: 'Mostly clearly',
                  },
                },
                {
                  value: 'Item 3',
                  text: {
                    default: 'Sometimes confusing',
                  },
                },
                {
                  value: 'Item 4',
                  text: {
                    default: 'Often confusing',
                  },
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'question6',
              title: {
                default: 'How fair was the teacher when grading or giving feedback?',
              },
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'Very fair',
                  },
                },
                {
                  value: 'Item 2',
                  text: {
                    default: 'Fair',
                  },
                },
                {
                  value: 'Item 3',
                  text: {
                    default: 'Sometimes unfair',
                  },
                },
                {
                  value: 'Item 4',
                  text: {
                    default: 'Often unfair',
                  },
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'question8',
              title: {
                default: 'Do you feel you learned a lot in this class?',
              },
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'Yes, definitely',
                  },
                },
                {
                  value: 'Item 2',
                  text: {
                    default: 'Somewhat',
                  },
                },
                {
                  value: 'Item 3',
                  text: {
                    default: 'Not really',
                  },
                },
                {
                  value: 'Item 4',
                  text: {
                    default: 'No',
                  },
                },
              ],
            },
            {
              type: 'rating',
              name: 'question7',
              title: {
                default: "What's you overall rating for the class",
              },
              rateType: 'stars',
            },
            {
              type: 'comment',
              name: 'question9',
              title: {
                default: 'Any feedback for the course or the teacher',
              },
            },
          ],
        },
      ],
    }),
  },

  // You can add more built-in templates here
]

export const defaultSurvey: TemplateType[] = [
  {
    id: 'default',
    name: 'Default Survey Template',
    description: 'A template for teacher feedback survey for end of semester.',
    json: JSON.stringify({
      title: {
        default: 'SurveyName',
      },
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
          elements: [
            {
              type: 'text',
              name: 'question1',
            },
          ],
        },
      ],
    }),
  },
]
